import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Typography,
  Grid,
  Paper,
  useTheme,
  Chip,
  Divider,
  Stack,
} from "@mui/material";
import Header from "../../components/Header";
import { getAllAppointments } from "../../services/appointmentService";
import {
  getAppointmentDoctorId,
  getAppointmentDoctorName,
  getAppointmentPatientId,
  getAppointmentPatientName,
} from "../../utils/appointmentDisplayUtils";
import dayjs from "dayjs";
import PersonIcon from "@mui/icons-material/Person";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";
import {
  extractTokenNumber,
  getTokenStatusUpper,
  hasAppointmentToken,
} from "../../utils/appointmentCancelUtils";

const TOKEN_REFRESH_MS = 30000;

const TokenDashboard = () => {
  const theme = useTheme();

  const [appointmentList, setAppointmentList] = useState([]);
  const [loading, setLoading] = useState(true);
  const fetchInFlight = useRef(false);

  const fetchData = async () => {
    if (fetchInFlight.current) return;
    fetchInFlight.current = true;
    setLoading(true);
    try {
      const appointmentsData = await getAllAppointments().catch((err) => {
        console.error("Error fetching appointments:", err);
        return [];
      });

      setAppointmentList(Array.isArray(appointmentsData) ? appointmentsData : []);
    } catch (error) {
      console.error("Error fetching data:", error);
      setAppointmentList([]);
    } finally {
      setLoading(false);
      fetchInFlight.current = false;
    }
  };

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, TOKEN_REFRESH_MS);
    return () => clearInterval(interval);
  }, []);

  const storedUser = localStorage.getItem("user");
  let isBaseUser = false;
  if (storedUser) {
    try {
      const user = JSON.parse(storedUser);
      isBaseUser = user.organizationId === null;
    } catch (e) {
      // ignore
    }
  }

  const filteredTokens = appointmentList
    .filter((app) => {
      if (!hasAppointmentToken(app)) return false;

      const aptDateValue =
        app.appointmentDateTime || app.appointmentDate || app.date;
      const isToday =
        aptDateValue &&
        new Date(aptDateValue).toDateString() === new Date().toDateString();

      // Base org: show any tokenized appointments
      if (isBaseUser) {
        return true;
      }

      // Hospital org: today's waiting + called tokens (called = green after doctor calls)
      const status = getTokenStatusUpper(app);
      return isToday && (status === "WAITING" || status === "CALLED");
    })
    .map((app) => ({
      tokenNumber: extractTokenNumber(app),
      tokenStatus: app.tokenStatus,
      appointmentId: app.id,
      patientId: getAppointmentPatientId(app),
      doctorId: getAppointmentDoctorId(app),
      appointmentDateTime: app.appointmentDateTime || app.appointmentDate || app.date,
      patientName: getAppointmentPatientName(app),
      doctorName: getAppointmentDoctorName(app),
    }))
    .sort((a, b) => Number(a.tokenNumber || 0) - Number(b.tokenNumber || 0));

  const getColor = (status) => {
    const statusUpper = String(status || "").toUpperCase();
    if (statusUpper === "WAITING") return "#FFA500"; // yellow / orange — waiting
    if (statusUpper === "CALLED") return "#4CAF50"; // green — doctor called
    if (statusUpper === "COMPLETED") return "#1e88e5";
    return "grey";
  };

  const getStatusLabel = (status) => {
    const statusUpper = String(status || "").toUpperCase();
    if (statusUpper === "WAITING") return "WAITING";
    if (statusUpper === "CALLED") return "CALLED";
    if (statusUpper === "COMPLETED") return "COMPLETED";
    return status || "—";
  };

  return (
    <Box sx={{ flexGrow: 1, p: 3 }}>
      {/* <Header title="TOKENS" /> */}

      {loading ? (
        <Typography>Loading tokens...</Typography>
      ) : filteredTokens.length === 0 ? (
        <Typography>No tokens available</Typography>
      ) : (
        <Grid container spacing={2}>
          {filteredTokens.map((token) => {
            const backgroundColor = getColor(token.tokenStatus);
            const aptDate = dayjs(token.appointmentDateTime);

            return (
              <Grid item xs={12} sm={6} md={4} lg={3} key={token.appointmentId}>
                <Paper
                  elevation={3}
                  sx={{
                    p: 1,
                    borderRadius: 2,
                    border: `2px solid ${backgroundColor}`,
                    backgroundColor: theme.palette.background.paper,
                    transition: "all 0.3s ease",
                    "&:hover": {
                      boxShadow: `0 6px 20px ${backgroundColor}40`,
                      transform: "translateY(-4px)",
                    },
                  }}
                >
                  <Stack spacing={0.5}>
                    <Box sx={{ textAlign: "center", pb: 0.25 }}>
                      <Chip
                        label={`Token #${token.tokenNumber}`}
                        sx={{
                          backgroundColor: backgroundColor,
                          color: "white",
                          fontWeight: "bold",
                          fontSize: "0.65rem",
                          height: "20px",
                          px: 0.5,
                        }}
                      />
                      <Chip
                        label={getStatusLabel(token.tokenStatus)}
                        size="small"
                        sx={{
                          ml: 0.5,
                          backgroundColor: backgroundColor,
                          color: "white",
                          opacity: 0.8,
                          fontSize: "0.55rem",
                          height: "18px",
                          px: 0.5,
                        }}
                      />
                    </Box>

                    <Divider sx={{ my: 0.25 }} />

                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, justifyContent: "space-between" }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.25, flex: 1, minWidth: 0 }}>
                        <PersonIcon fontSize="small" color="primary" sx={{ fontSize: "0.7rem" }} />
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.55rem", lineHeight: 1 }}>
                            Patient
                          </Typography>
                          <Typography variant="body2" fontWeight={600} sx={{ fontSize: "0.7rem", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", lineHeight: 1.2 }}>
                            {token.patientName}
                          </Typography>
                        </Box>
                      </Box>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.25, flex: 1, minWidth: 0 }}>
                        <LocalHospitalIcon fontSize="small" color="primary" sx={{ fontSize: "0.7rem" }} />
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.55rem", lineHeight: 1 }}>
                            Doctor
                          </Typography>
                          <Typography variant="body2" fontWeight={600} sx={{ fontSize: "0.7rem", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", lineHeight: 1.2 }}>
                            {token.doctorName}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>

                    <Divider sx={{ my: 0.25 }} />

                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, justifyContent: "space-between" }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.25, flex: 1, minWidth: 0 }}>
                        <CalendarTodayIcon fontSize="small" color="action" sx={{ fontSize: "0.7rem" }} />
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.55rem", lineHeight: 1 }}>
                            Date
                          </Typography>
                          <Typography variant="body2" sx={{ fontSize: "0.7rem", lineHeight: 1.2 }}>
                            {aptDate.format("DD MMM YYYY")}
                          </Typography>
                        </Box>
                      </Box>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.25, flex: 1, minWidth: 0 }}>
                        <AccessTimeIcon fontSize="small" color="action" sx={{ fontSize: "0.7rem" }} />
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.55rem", lineHeight: 1 }}>
                            Time
                          </Typography>
                          <Typography variant="body2" sx={{ fontSize: "0.7rem", lineHeight: 1.2 }}>
                            {aptDate.format("hh:mm A")}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  </Stack>
                </Paper>
              </Grid>
            );
          })}
        </Grid>
      )}
    </Box>
  );
};

export default TokenDashboard;
